import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { drawService } from '../services/drawService';
import { Draw, DrawWinner } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Icon } from '../components/participant/Icon';
import { participantColors as c, fadeUpKeyframes } from '../theme/participantTheme';
import logo from '../logo.png';

const liveKeyframes = `
${fadeUpKeyframes}
@keyframes participantLivePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
@keyframes participantDiceRoll { 0% { transform: rotate(0deg) scale(1); } 25% { transform: rotate(90deg) scale(1.12); } 50% { transform: rotate(180deg) scale(1); } 75% { transform: rotate(270deg) scale(1.12); } 100% { transform: rotate(360deg) scale(1); } }
@keyframes participantTicketFloat { 0%, 100% { transform: translateY(0) rotate(-1.5deg); } 50% { transform: translateY(-8px) rotate(1.5deg); } }
@keyframes participantWinnerReveal { 0% { transform: scale(0.5) rotate(-8deg); opacity: 0; } 60% { transform: scale(1.08) rotate(4deg); opacity: 1; } 100% { transform: scale(1) rotate(0deg); opacity: 1; } }
@keyframes participantGlowPulse { 0%, 100% { box-shadow: 0 0 24px rgba(245,176,27,0.35); } 50% { box-shadow: 0 0 60px rgba(245,176,27,0.7); } }
@keyframes participantConfettiFall { 0% { transform: translateY(-40px) rotate(0deg); opacity: 1; } 100% { transform: translateY(105vh) rotate(720deg); opacity: 0; } }
`;

const CONFETTI_COLORS = [c.amber, c.brandMid, c.brandLight, '#e79a00', c.onDarkPrimary, '#00b33c'];

const Confetti: React.FC = () => {
  const pieces = React.useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        left: Math.random() * 100,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        duration: 2.5 + Math.random() * 3,
        delay: Math.random() * 2.5,
        size: 8 + (i % 3) * 4,
        round: i % 2 === 0,
      })),
    []
  );

  return (
    <Box sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 1400 }}>
      {pieces.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: p.round ? '50%' : '2px',
            animation: `participantConfettiFall ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </Box>
  );
};

type Phase = 'idle' | 'prep' | 'shuffle' | 'reveal' | 'winner';

const dedupeWinners = (winners: DrawWinner[]): DrawWinner[] => {
  const seen = new Set<string>();
  return winners.filter((winner) => {
    if (seen.has(winner.id)) return false;
    seen.add(winner.id);
    return true;
  });
};

const DrawLive: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError, showWarning, showSuccess } = useToast();

  const [draw, setDraw] = useState<Draw | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [randomNumber, setRandomNumber] = useState('00000');
  const [sessionWinners, setSessionWinners] = useState<DrawWinner[]>([]);
  const winnerSectionRef = React.useRef<HTMLDivElement>(null);

  const isAssociation = user?.role === 'ASSOCIACAO';
  const isDrawing = phase === 'prep' || phase === 'shuffle' || phase === 'reveal';
  const currentWinner = sessionWinners[0] || null;
  const history = sessionWinners.slice(1);

  const validWinners = dedupeWinners(
    draw?.winners?.filter((w) => w.status === 'APPROVED' || w.status === 'PENDING') || [],
  );
  const approvedCount = validWinners.length;
  const maxWinners = draw?.maxWinners || 0;
  const isCancelled = draw?.status === 'CANCELLED';
  const isCompleted = !isCancelled && (draw?.status === 'COMPLETED' || (maxWinners > 0 && approvedCount >= maxWinners));
  const canDraw = isAssociation && !isDrawing && !isCompleted && !isCancelled;

  useEffect(() => {
    const fetchDrawData = async () => {
      try {
        const [drawData, winnersData] = await Promise.all([
          drawService.getDrawById(id!),
          drawService.getDrawWinners(id!),
        ]);
        setDraw(drawData);

        const activeWinners = dedupeWinners(
          winnersData.filter((w) => w.status === 'APPROVED' || w.status === 'PENDING'),
        );
        if (activeWinners.length > 0) {
          setSessionWinners(activeWinners);
          setPhase('winner');
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchDrawData();
  }, [id]);

  useEffect(() => {
    if (phase === 'prep' || phase === 'shuffle') {
      const interval = setInterval(() => {
        setRandomNumber(String(Math.floor(Math.random() * 100000)).padStart(5, '0'));
      }, 90);
      return () => clearInterval(interval);
    }
  }, [phase]);

  const performDraw = async () => {
    if (!draw || isDrawing) return;

    setPhase('prep');

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setPhase('shuffle');
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const winnersData = await drawService.executeDraw(id!);

      if (winnersData.length > 0) {
        setPhase('reveal');
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Fonte da verdade: lista completa da API (winningDate DESC).
        // Evita duplicar ganhadores pré-carregados ao prependar só o novo.
        const activeWinners = dedupeWinners(
          winnersData.filter((w) => w.status === 'APPROVED' || w.status === 'PENDING'),
        );
        const lastWinner = activeWinners[0];
        setSessionWinners(activeWinners);
        setPhase('winner');

        showSuccess(`🎉 Ganhador sorteado com sucesso: ${lastWinner?.user?.name || 'Participante'}!`);

        setTimeout(() => {
          winnerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 400);

        const updatedDraw = await drawService.getDrawById(id!);
        setDraw(updatedDraw);
      } else {
        setPhase('idle');
        showWarning('Nenhum ganhador foi sorteado. Verifique se há cupons válidos para esta campanha.');
      }
    } catch (error: any) {
      setPhase(sessionWinners.length > 0 ? 'winner' : 'idle');

      let errorMessage = error.response?.data?.message || error.message || 'Erro ao realizar sorteio. Tente novamente.';

      if (errorMessage.includes('máxima de prêmios já foi sorteada')) {
        showWarning('⚠️ Todos os prêmios deste sorteio já foram distribuídos!');
      } else if (errorMessage.includes('não encontrado')) {
        showError('❌ Sorteio não encontrado. Verifique se o ID está correto.');
      } else if (errorMessage.includes('não possui cupons')) {
        showWarning('⚠️ Não há cupons válidos para realizar o sorteio.');
      } else if (errorMessage.includes('cancelado')) {
        showWarning('⚠️ Este sorteio foi cancelado e não pode ser realizado.');
      } else if (errorMessage.includes('já foi realizado')) {
        showWarning('⚠️ Este sorteio já foi realizado anteriormente.');
      } else {
        showError(`❌ ${errorMessage}`);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: c.gradients.pageBg }}>
        <CircularProgress sx={{ color: c.brandMid }} size={48} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: c.gradients.pageBg,
        fontFamily: c.fontFamily,
        color: c.textBase,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0 16px 48px 16px',
      }}
    >
      <style>{liveKeyframes}</style>

      {phase === 'winner' && <Confetti />}

      <Box sx={{ width: '100%', maxWidth: 760, display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {/* Top bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', pt: '18px', px: '4px' }}>
          <Box component="img" src={logo} alt="ACIAG" sx={{ height: 44, width: 'auto', objectFit: 'contain' }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: c.surfaceDark,
              borderRadius: `${c.radius.pill}px`,
              padding: '9px 18px',
              boxShadow: '0 2px 10px rgba(20,61,24,0.15)',
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: c.amber,
                animation: 'participantLivePulse 1.2s ease infinite',
              }}
            />
            <Box component="span" sx={{ fontSize: 15, fontWeight: 800, color: c.onDarkPrimary, letterSpacing: '0.08em' }}>
              AO VIVO
            </Box>
          </Box>
        </Box>

        {/* Page header */}
        <Box
          sx={{
            background: c.surface,
            borderRadius: `${c.radius.card}px`,
            padding: '20px',
            boxShadow: c.shadows.card,
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
          }}
        >
          <Box
            component="button"
            onClick={() => navigate('/draws')}
            title="Voltar para sorteios"
            sx={{
              width: 52,
              height: 52,
              minWidth: 52,
              borderRadius: `${c.radius.button}px`,
              border: `2px solid ${c.border}`,
              background: c.surfaceHover,
              color: c.brandDark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { background: '#e7f4e7', borderColor: c.borderHover },
            }}
          >
            <Icon name="arrow_back" size={26} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
            <Box component="div" sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.15, color: c.textDarkest }}>
              Sorteio ao Vivo
            </Box>
            <Box component="div" sx={{ fontSize: 16, color: c.textSecondary, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {draw?.campaign?.name && draw?.prize?.name
                ? `${draw.campaign.name} · ${draw.prize.name}`
                : 'Carregando informações do sorteio...'}
            </Box>
          </Box>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: c.amberBg,
              color: c.amberTextDark,
              borderRadius: `${c.radius.pill}px`,
              padding: '7px 15px',
              fontSize: 14,
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            <Icon name="emoji_events" size={17} />
            Sorteados: {approvedCount}/{maxWinners}
          </Box>
        </Box>

        {/* Stage */}
        <Box
          ref={winnerSectionRef}
          sx={{
            background: c.surfaceDark,
            borderRadius: `${c.radius.cardLarge}px`,
            padding: '40px 28px',
            color: c.onDarkPrimary,
            boxShadow: '0 12px 40px rgba(6,63,16,0.35)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '22px',
            textAlign: 'center',
            animation: 'participantFadeUp 0.4s ease both',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box component="div" sx={{ fontSize: 15, fontWeight: 800, color: c.onDarkAccent, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Prêmio da vez
            </Box>
            <Box component="div" sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15 }}>
              {draw?.prize?.name}
            </Box>
            <Box component="div" sx={{ fontSize: 17, color: c.onDarkMuted, fontWeight: 600 }}>
              {draw?.campaign?.name}
            </Box>
          </Box>

          {/* Idle */}
          {phase === 'idle' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', padding: '10px 0' }}>
              <Box
                sx={{
                  width: 110,
                  height: 110,
                  borderRadius: '28px',
                  background: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="casino" size={62} color={c.amber} />
              </Box>
              <Box component="div" sx={{ fontSize: 21, fontWeight: 800 }}>
                Pronto para sortear!
              </Box>
              <Box component="div" sx={{ fontSize: 16, color: c.onDarkMuted, maxWidth: 420, lineHeight: 1.5 }}>
                {isAssociation
                  ? 'Todos os cupons cadastrados na campanha participam. Boa sorte a todos!'
                  : 'Aguarde, o sorteio vai começar em breve!'}
              </Box>
            </Box>
          )}

          {/* Drawing (prep / shuffle) */}
          {(phase === 'prep' || phase === 'shuffle') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '10px 0' }}>
              <Icon name="casino" size={74} color={c.amber} style={{ animation: 'participantDiceRoll 0.7s linear infinite' }} />
              <Box
                component="div"
                sx={{
                  fontSize: 88,
                  fontWeight: 800,
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.12em',
                  lineHeight: 1,
                  color: c.onDarkPrimary,
                  textShadow: '0 0 30px rgba(245,176,27,0.7)',
                }}
              >
                {randomNumber}
              </Box>
              <Box component="div" sx={{ fontSize: 21, fontWeight: 800 }}>
                {phase === 'prep' ? '🎲 Preparando o sorteio...' : '🎰 Sorteando...'}
              </Box>
              <Box component="div" sx={{ fontSize: 16, color: c.onDarkMuted }}>
                {phase === 'prep' ? 'Embaralhando os cupons participantes' : 'Aguarde, estamos selecionando o ganhador!'}
              </Box>
            </Box>
          )}

          {/* Reveal */}
          {phase === 'reveal' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '10px 0' }}>
              <Icon name="celebration" size={72} color={c.amber} />
              <Box component="div" sx={{ fontSize: 24, fontWeight: 800 }}>
                🎉 Temos um ganhador!
              </Box>
              <CircularProgress size={40} sx={{ color: c.onDarkPrimary }} />
            </Box>
          )}

          {/* Winner */}
          {phase === 'winner' && currentWinner && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '6px 0', animation: 'participantWinnerReveal 0.8s ease-out both', width: '100%' }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(245,176,27,0.18)',
                  color: c.amber,
                  borderRadius: `${c.radius.pill}px`,
                  padding: '8px 20px',
                  fontSize: 15,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                }}
              >
                <Icon name="celebration" size={19} />
                GANHADOR SORTEADO
              </Box>
              <Box component="div" sx={{ fontSize: 42, fontWeight: 800, lineHeight: 1.1 }}>
                {currentWinner.user?.name || 'Nome não disponível'}
              </Box>

              {/* Golden ticket */}
              <Box
                sx={{
                  position: 'relative',
                  background: 'linear-gradient(135deg, #f5b01b 0%, #e79a00 100%)',
                  border: '3px dashed rgba(61,42,0,0.35)',
                  borderRadius: `${c.radius.card}px`,
                  padding: '24px 44px',
                  color: '#3d2a00',
                  minWidth: 300,
                  animation: 'participantTicketFloat 3s ease-in-out infinite, participantGlowPulse 2s ease infinite',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                  <Icon name="confirmation_number" size={18} />
                  Cupom premiado
                </Box>
                <Box component="div" sx={{ fontSize: 64, fontWeight: 800, fontFamily: "'Courier New', monospace", letterSpacing: '0.1em', lineHeight: 1.1 }}>
                  {currentWinner.winningCoupon?.number ?? 'N/A'}
                </Box>
                <Box component="div" sx={{ fontSize: 12.5, fontWeight: 800, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.2em' }}>
                  ★ Número da sorte ★
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {currentWinner.winningCoupon?.store?.name && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'rgba(255,255,255,0.1)',
                      color: c.onDarkSecondary,
                      borderRadius: `${c.radius.pill}px`,
                      padding: '8px 16px',
                      fontSize: 14.5,
                      fontWeight: 700,
                    }}
                  >
                    <Icon name="storefront" size={17} color={c.onDarkAccent} />
                    {currentWinner.winningCoupon.store.name}
                  </Box>
                )}
                {currentWinner.winningCoupon?.sellerName && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'rgba(255,255,255,0.1)',
                      color: c.onDarkSecondary,
                      borderRadius: `${c.radius.pill}px`,
                      padding: '8px 16px',
                      fontSize: 14.5,
                      fontWeight: 700,
                    }}
                  >
                    <Icon name="person" size={17} color={c.onDarkAccent} />
                    Vendedor: {currentWinner.winningCoupon.sellerName}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Draw button */}
          {canDraw && (
            <Box
              component="button"
              onClick={performDraw}
              sx={{
                border: 'none',
                borderRadius: `${c.radius.button}px`,
                background: 'linear-gradient(135deg, #f5b01b, #e79a00)',
                color: '#3d2a00',
                fontFamily: c.fontFamily,
                padding: '18px 42px',
                fontSize: 20,
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 10px 30px rgba(245,176,27,0.35)',
                transition: 'transform 0.15s',
                '&:hover': { transform: 'translateY(-3px)' },
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              <Icon name="play_arrow" size={26} />
              {sessionWinners.length === 0 ? 'Sortear ganhador' : 'Sortear próximo ganhador'}
            </Box>
          )}

          {isCancelled && !isDrawing && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                background: 'rgba(255,100,100,0.15)',
                color: '#ffb4b4',
                borderRadius: `${c.radius.pill}px`,
                padding: '10px 22px',
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              <Icon name="block" size={20} />
              Sorteio cancelado
            </Box>
          )}

          {isCompleted && !isDrawing && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                background: 'rgba(127,216,138,0.15)',
                color: c.onDarkAccent,
                borderRadius: `${c.radius.pill}px`,
                padding: '10px 22px',
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              <Icon name="check_circle" size={20} />
              Sorteio concluído — todos os prêmios distribuídos
            </Box>
          )}
        </Box>

        {/* Previous winners this session */}
        {history.length > 0 && (
          <>
            <Box sx={{ fontSize: 18, fontWeight: 800, color: c.textDarkest, padding: '6px 4px 0 4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name="emoji_events" size={22} color={c.brandMid} />
              Ganhadores deste sorteio
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {history.map((w, i) => (
                <Box
                  key={w.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '13px',
                    background: c.surface,
                    borderRadius: `${c.radius.card}px`,
                    padding: '15px 17px',
                    boxShadow: c.shadows.listItem,
                    animation: `participantFadeUp ${0.3 + i * 0.05}s ease both`,
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      minWidth: 46,
                      borderRadius: '50%',
                      background: c.amber,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="emoji_events" size={26} color={c.onDarkPrimary} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box component="div" sx={{ fontSize: 17, fontWeight: 800, color: c.textDarkest, lineHeight: 1.2 }}>
                      {w.user?.name || 'Ganhador'}
                    </Box>
                    <Box component="div" sx={{ fontSize: 14, color: c.textSecondary, fontWeight: 600 }}>
                      {w.winningCoupon?.store?.name}
                      {w.winningCoupon?.sellerName ? ` · Vendedor: ${w.winningCoupon.sellerName}` : ''}
                    </Box>
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: c.amberTextDark,
                      background: c.amberBg,
                      borderRadius: '12px',
                      padding: '8px 14px',
                      fontFamily: "'Courier New', monospace",
                    }}
                  >
                    {w.winningCoupon?.number ?? 'N/A'}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DrawLive;
